import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, TextInput, Platform } from 'react-native';
import { useAppTheme } from '../../utils/useAppTheme';
import {
  getResearchCollection,
  deleteResearchEntry,
  shareResearchCollection,
  getResearchStats,
  updateResearchEntry,
  getResearchSourceInfo,
} from '../../utils/researchStorage';
import { copyToClipboard, shortenUrl } from '../../utils/shareUtils';

// Default cipher names for quick preview
const DEFAULT_PREVIEW_CIPHERS = [
  'English Ordinal',
  'Full Reduction',
  'Reverse Ordinal',
  'Reverse Full Reduction',
];
const HEBREW_PREVIEW_CIPHERS = [
  'Mispar Hechrachi',
  'Mispar Gadol',
  'Mispar Siduri',
  'Mispar Katan',
];

const SORT_OPTIONS = [
  { key: 'newest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'az', label: 'A-Z' },
  { key: 'za', label: 'Z-A' },
];

export default function ResearchCollection({ 
  currentText, 
  currentCiphers, 
  currentResults,
  onLoadResearch,
  onLoadCompare
}) {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [research, setResearch] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  useEffect(() => {
    loadResearch();
  }, []);

  const loadResearch = async () => {
    const data = await getResearchCollection();
    setResearch(data);
    const statsData = await getResearchStats();
    setStats(statsData);
  };

  const handleDelete = async (id) => {
    if (confirm('Delete this research entry?')) {
      await deleteResearchEntry(id);
      loadResearch();
    }
  };

  const handleDeleteAll = async () => {
    if (confirm('Delete ALL research entries? This cannot be undone!')) {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('gematria_research_collection');
      }
      loadResearch();
    }
  };

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  // Get user-visible note (strip compare metadata)
  const getUserNote = (entry) => {
    if (!entry.note) return '';
    if (entry.note.startsWith('[COMPARE]') || entry.note.startsWith('[COMPARE_NUMBERS]')) {
      const parts = entry.note.split('|||');
      return parts.length > 2 ? parts.slice(2).join('|||') : '';
    }
    return entry.note;
  };

  const handleEditNote = (entry) => {
    setEditingNoteId(entry.id);
    setEditingNoteText(getUserNote(entry));
  };

  const handleSaveNote = async (entry) => {
    let noteToSave = editingNoteText.trim();
    if (entry.note && (entry.note.startsWith('[COMPARE]') || entry.note.startsWith('[COMPARE_NUMBERS]'))) {
      const parts = entry.note.split('|||');
      const prefix = parts.slice(0, 2).join('|||');
      noteToSave = noteToSave ? `${prefix}|||${noteToSave}` : prefix;
    }
    await updateResearchEntry(entry.id, { note: noteToSave });
    setEditingNoteId(null);
    setEditingNoteText('');
    loadResearch();
  };

  const handleLoad = (entry) => {
    if (entry.type === 'date') {
      // For web, we navigate to the dates page with the entry ID in the URL
      // The DateCalculatorPage will then load this from storage
      window.location.href = `/dates?load=${entry.id}`;
      return;
    }
    if (entry.tags && entry.tags.includes('compare') && entry.note) {
      if (entry.note.startsWith('[COMPARE]')) {
        const otherPhrase = entry.note.split('|||')[0].replace('[COMPARE]', '');
        if (onLoadCompare) {
          onLoadCompare(entry.text, otherPhrase);
        }
        return;
      }
      if (entry.note.startsWith('[COMPARE_NUMBERS]')) {
        const otherPhrase = entry.note.split('|||')[0].replace('[COMPARE_NUMBERS]', '');
        if (onLoadCompare) {
          onLoadCompare(entry.text, otherPhrase);
        }
        return;
      }
    }
    if (entry.tags && entry.tags.includes('number')) {
      window.location.href = `/number?num=${entry.text}`;
      return;
    }
    onLoadResearch(entry.text, entry.selectedCiphers);
  };

  const handleShareCollection = async () => {
    if (research.length === 0) {
      alert('No research to share');
      return;
    }

    const encoded = shareResearchCollection(research);
    const baseUrl = window.location.origin;
    const longUrl = `${baseUrl}?collection=${encoded}`;
    
    const originalButton = document.activeElement;
    if (originalButton) {
      originalButton.textContent = '⏳ Shortening...';
      originalButton.disabled = true;
    }
    
    const shortUrl = await shortenUrl(longUrl);
    const success = await copyToClipboard(shortUrl);
    
    if (originalButton) {
      originalButton.textContent = '📤 Share All';
      originalButton.disabled = false;
    }
    
    if (success) {
      alert(`Short link copied to clipboard!\n\n${shortUrl}\n\nShare this link to share your entire research collection.`);
    } else {
      alert('Failed to copy link. URL: ' + shortUrl);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getPreviewCiphers = (entry) => {
    if (!entry.results || !Array.isArray(entry.results) || entry.results.length === 0) return [];
    const isHebrew = entry.results.some(r => HEBREW_PREVIEW_CIPHERS.includes(r.name));
    const previewNames = isHebrew ? HEBREW_PREVIEW_CIPHERS : DEFAULT_PREVIEW_CIPHERS;

    const previews = [];
    for (const name of previewNames) {
      const result = entry.results.find(r => r.name === name);
      if (result) {
        let shortName = name;
        if (name === 'English Ordinal') shortName = 'Ordinal';
        else if (name === 'Full Reduction') shortName = 'Reduction';
        else if (name === 'Reverse Ordinal') shortName = 'Rev. Ord.';
        else if (name === 'Reverse Full Reduction') shortName = 'Rev. Red.';
        else if (name === 'Mispar Hechrachi') shortName = 'Standard';
        else if (name === 'Mispar Gadol') shortName = 'Gadol';
        else if (name === 'Mispar Siduri') shortName = 'Ordinal';
        else if (name === 'Mispar Katan') shortName = 'Reduced';
        previews.push({ name: shortName, value: result.totalValue });
      }
    }
    return previews;
  };

  const getMatchInfo = (entry) => {
    if (!entry.note || !entry.note.includes('|||')) return null;
    const parts = entry.note.split('|||');
    if (parts.length < 2) return null;
    return parts[1];
  };

  const getFilteredResearch = () => {
    let filtered = research;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(entry => {
        const textMatch = entry.text.toLowerCase().includes(q);
        const noteMatch = entry.note && entry.note.toLowerCase().includes(q);
        const tagMatch = entry.tags && entry.tags.some(t => t.toLowerCase().includes(q));
        const valueMatch = entry.results && entry.results.some(r => 
          r.totalValue.toString() === searchQuery.trim()
        );
        return textMatch || noteMatch || tagMatch || valueMatch;
      });
    }

    const sorted = [...filtered];
    switch (sortBy) {
      case 'oldest': sorted.sort((a, b) => a.timestamp - b.timestamp); break;
      case 'az': sorted.sort((a, b) => a.text.localeCompare(b.text)); break;
      case 'za': sorted.sort((a, b) => b.text.localeCompare(a.text)); break;
      default: sorted.sort((a, b) => b.timestamp - a.timestamp); break;
    }
    return sorted;
  };

  const filteredResearch = getFilteredResearch();

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>Research List</Text>
          {stats && (
            <Text style={styles.countBadge}>
              {stats.totalEntries}/{stats.maxEntries}
            </Text>
          )}
        </View>
        {stats && stats.percentFull >= 80 && (
          <Text style={styles.warningText}>⚠️ Storage {stats.percentFull}% full</Text>
        )}
      </View>

      {/* Search & Sort */}
      <View style={styles.searchSortBar}>
        <View style={styles.searchContainer}>
          <Text style={styles.searchIconText}>🔍</Text>
          <TextInput
            style={styles.searchInput}
            placeholder="Search phrases, values, tags..."
            placeholderTextColor={colors.lightText}
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoCapitalize="none"
            autoCorrect={false}
          />
        </View>
        <View style={styles.sortRow}>
          {SORT_OPTIONS.map(opt => (
            <TouchableOpacity
              key={opt.key}
              style={[styles.sortChip, sortBy === opt.key && styles.sortChipActive]}
              onPress={() => setSortBy(opt.key)}
            >
              <Text style={[styles.sortChipText, sortBy === opt.key && styles.sortChipTextActive]}>
                {opt.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Action Buttons */}
      <View style={styles.actions}>
        <TouchableOpacity style={styles.actionButton} onPress={handleShareCollection}>
          <Text style={styles.actionButtonText}>📤 Share All</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.actionButton, styles.deleteAllButton]} onPress={handleDeleteAll}>
          <Text style={[styles.actionButtonText, styles.deleteAllButtonText]}>🗑️ Delete All</Text>
        </TouchableOpacity>
      </View>

      {/* Research List */}
      <ScrollView style={styles.list}>
        {searchQuery.trim() && filteredResearch.length > 0 && (
          <Text style={styles.filterCount}>
            {filteredResearch.length} result{filteredResearch.length !== 1 ? 's' : ''} found
          </Text>
        )}

        {filteredResearch.length === 0 ? (
          <View style={styles.emptyState}>
            {searchQuery.trim() ? (
              <>
                <Text style={styles.emptyIcon}>🔍</Text>
                <Text style={styles.emptyText}>No matches for "{searchQuery}"</Text>
                <Text style={styles.emptySubtext}>Try a different search term or number</Text>
              </>
            ) : (
              <>
                <Text style={styles.emptyIcon}>📝</Text>
                <Text style={styles.emptyText}>No research saved yet</Text>
                <Text style={styles.emptySubtext}>
                  Enter text and click "Save Current" to start building your research collection
                </Text>
              </>
            )}
          </View>
        ) : (
          filteredResearch.map((entry) => {
            const isExpanded = expandedIds.has(entry.id);
            const previewCiphers = getPreviewCiphers(entry);
            const isCompare = entry.tags && entry.tags.includes('compare') && entry.note && (entry.note.startsWith('[COMPARE]') || entry.note.startsWith('[COMPARE_NUMBERS]'));
            const matchInfo = isCompare ? getMatchInfo(entry) : null;
            const cipherCount = Array.isArray(entry.results) ? entry.results.length : 0;
            const sourceInfo = getResearchSourceInfo(entry);

            return (
              <View key={entry.id} style={styles.entryCard}>
                {/* Header */}
                <TouchableOpacity 
                  style={styles.entryHeader}
                  onPress={() => toggleExpand(entry.id)}
                >
                  <View style={styles.entryHeaderContent}>
                    <View style={styles.sourceBadge}>
                      <Text style={styles.sourceBadgeText}>{sourceInfo.icon} {sourceInfo.label}</Text>
                    </View>
                    <Text style={styles.entryText} numberOfLines={isCompare ? 1 : 2}>
                      {entry.text}
                    </Text>
                    {isCompare ? (
                      <Text style={styles.compareVsText} numberOfLines={1}>
                        vs {entry.note.split('|||')[0].replace('[COMPARE]', '').replace('[COMPARE_NUMBERS]', '')}
                      </Text>
                    ) : null}
                    {matchInfo ? (
                      <Text style={styles.matchInfoText} numberOfLines={1}>
                        🎯 {matchInfo}
                      </Text>
                    ) : null}
                    {entry.note && !entry.note.startsWith('[COMPARE]') && !entry.note.startsWith('[COMPARE_NUMBERS]') ? (
                      <Text style={styles.entryNote} numberOfLines={1}>💭 {entry.note}</Text>
                    ) : null}
                    <View style={styles.entryMeta}>
                      <Text style={styles.entryDate}>{formatDate(entry.timestamp)}</Text>
                      {entry.type !== 'date' && (
                        <Text style={styles.cipherCountText}>{cipherCount} ciphers</Text>
                      )}
                    </View>
                  </View>
                  <Text style={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</Text>
                </TouchableOpacity>

                {/* Quick Preview - 4 default ciphers (for words) or Date Summary (for dates) */}
                {entry.type === 'date' ? (
                  <View style={styles.quickResults}>
                    <View style={styles.quickResultItem}>
                      <Text style={styles.quickResultValue}>{entry.results.totalDays.toLocaleString()}</Text>
                      <Text style={styles.quickResultName}>Total Days</Text>
                    </View>
                    <View style={[styles.quickResultItem, { flex: 2 }]}>
                      <Text style={styles.quickResultValue} numberOfLines={1}>
                        {new Date(entry.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </Text>
                      <Text style={styles.quickResultName}>Start Date</Text>
                    </View>
                    <View style={[styles.quickResultItem, { flex: 2 }]}>
                      <Text style={styles.quickResultValue} numberOfLines={1}>
                        {new Date(entry.endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </Text>
                      <Text style={styles.quickResultName}>End Date</Text>
                    </View>
                  </View>
                ) : previewCiphers.length > 0 ? (
                  <View style={styles.quickResults}>
                    {previewCiphers.map((cipher, idx) => (
                      <View key={idx} style={styles.quickResultItem}>
                        <Text style={styles.quickResultValue}>{cipher.value}</Text>
                        <Text style={styles.quickResultName}>{cipher.name}</Text>
                      </View>
                    ))}
                  </View>
                ) : null}

                {/* Tags */}
                {entry.tags && entry.tags.length > 0 ? (
                  <View style={styles.tags}>
                    {entry.tags.map((tag, idx) => (
                      <View key={idx} style={styles.tag}>
                        <Text style={styles.tagText}>#{tag}</Text>
                      </View>
                    ))}
                  </View>
                ) : null}

                {/* Expanded View */}
                {isExpanded ? (
                  <View style={styles.expandedContent}>
                    {entry.type === 'date' ? (
                      <View>
                        <Text style={styles.expandedTitle}>Duration Variations</Text>
                        <View style={styles.detailedResults}>
                          <View style={styles.detailedResultItem}>
                            <View style={styles.detailedResultHeader}>
                              <Text style={styles.detailedResultName}>Years, Months, Days</Text>
                              <Text style={styles.detailedResultValue}>
                                {entry.results.ymd.years}y {entry.results.ymd.months}m {entry.results.ymd.days}d
                              </Text>
                            </View>
                          </View>
                          <View style={styles.detailedResultItem}>
                            <View style={styles.detailedResultHeader}>
                              <Text style={styles.detailedResultName}>Years, Days</Text>
                              <Text style={styles.detailedResultValue}>
                                {entry.results.yearsOnly}y {entry.results.remainingDaysAfterYears}d
                              </Text>
                            </View>
                          </View>
                          <View style={styles.detailedResultItem}>
                            <View style={styles.detailedResultHeader}>
                              <Text style={styles.detailedResultName}>Years, Weeks, Days</Text>
                              <Text style={styles.detailedResultValue}>
                                {entry.results.ywd.years}y {entry.results.ywd.weeks}w {entry.results.ywd.days}d
                              </Text>
                            </View>
                          </View>
                          <View style={styles.detailedResultItem}>
                            <View style={styles.detailedResultHeader}>
                              <Text style={styles.detailedResultName}>Months, Days</Text>
                              <Text style={styles.detailedResultValue}>
                                {entry.results.totalMonths}m {entry.results.remainingDaysAfterMonths}d
                              </Text>
                            </View>
                          </View>
                          <View style={styles.detailedResultItem}>
                            <View style={styles.detailedResultHeader}>
                              <Text style={styles.detailedResultName}>Weeks, Days</Text>
                              <Text style={styles.detailedResultValue}>
                                {entry.results.totalWeeks}w {entry.results.remainingDaysAfterWeeks}d
                              </Text>
                            </View>
                          </View>
                        </View>
                      </View>
                    ) : entry.results && entry.results.length > 0 ? (
                      <View>
                        <Text style={styles.expandedTitle}>All Results ({entry.results.length})</Text>
                        <ScrollView style={styles.detailedResults} nestedScrollEnabled>
                          {entry.results.map((result, idx) => (
                            <View key={idx} style={styles.detailedResultItem}>
                              <View style={styles.detailedResultHeader}>
                                <Text style={styles.detailedResultName}>{result.name}</Text>
                                <Text style={styles.detailedResultValue}>{result.totalValue}</Text>
                              </View>
                              {result.wordValues && result.wordValues.length > 0 ? (
                                <View style={styles.wordBreakdownMini}>
                                  {result.wordValues.map((word, widx) => (
                                    <Text key={widx} style={styles.wordBreakdownText}>
                                      {word.word}: {word.value}
                                    </Text>
                                  ))}
                                </View>
                              ) : null}
                            </View>
                          ))}
                        </ScrollView>
                      </View>
                    ) : null}
                  </View>
                ) : null}

                {/* Actions */}
                <View style={styles.entryActions}>
                  <TouchableOpacity
                    style={styles.noteButton}
                    onPress={() => handleEditNote(entry)}
                  >
                    <Text style={[styles.noteButtonText, getUserNote(entry) && styles.noteButtonTextActive]}>
                      {getUserNote(entry) ? '📝 Note' : '📝 Add Note'}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={styles.loadButton}
                    onPress={() => handleLoad(entry)}
                  >
                    <Text style={styles.loadButtonText}>📥 Load</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={styles.deleteEntryButton}
                    onPress={() => handleDelete(entry.id)}
                  >
                    <Text style={styles.deleteEntryText}>🗑️</Text>
                  </TouchableOpacity>
                </View>

                {/* Note editor */}
                {editingNoteId === entry.id ? (
                  <View style={styles.noteEditContainer}>
                    <TextInput
                      style={styles.noteInput}
                      value={editingNoteText}
                      onChangeText={setEditingNoteText}
                      placeholder="Write a note..."
                      placeholderTextColor={colors.lightText}
                      multiline
                      autoFocus
                    />
                    <View style={styles.noteEditActions}>
                      <TouchableOpacity onPress={() => setEditingNoteId(null)} style={styles.noteCancelBtn}>
                        <Text style={styles.noteCancelText}>Cancel</Text>
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => handleSaveNote(entry)} style={styles.noteSaveBtn}>
                        <Text style={styles.noteSaveText}>Save</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : getUserNote(entry) ? (
                  <TouchableOpacity
                    style={styles.noteDisplay}
                    onPress={() => handleEditNote(entry)}
                  >
                    <Text style={styles.noteDisplayText}>📝 {getUserNote(entry)}</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            );
          })
        )}
      </ScrollView>
    </View>
  );
}

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    backgroundColor: colors.surface,
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
  },
  countBadge: {
    fontSize: 12,
    color: colors.onPrimary,
    backgroundColor: colors.primary,
    paddingHorizontal: 10,
    paddingVertical: 2,
    borderRadius: 10,
    overflow: 'hidden',
    fontWeight: '600',
  },
  warningText: {
    fontSize: 11,
    color: colors.warning,
    marginTop: 4,
    fontWeight: '600',
  },
  searchSortBar: {
    backgroundColor: colors.surface,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.inputBackground,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 8,
    marginBottom: 8,
  },
  searchIconText: {
    fontSize: 14,
    marginRight: 6,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 8,
    fontSize: 14,
    color: colors.text,
    backgroundColor: colors.inputBackground,
    borderWidth: 0,
    borderColor: colors.border,
    outlineStyle: 'none',
  },
  sortRow: {
    flexDirection: 'row',
    gap: 6,
  },
  sortChip: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: colors.mutedSurface,
  },
  sortChipActive: {
    backgroundColor: colors.primary,
  },
  sortChipText: {
    fontSize: 11,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  sortChipTextActive: {
    color: colors.onPrimary,
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
    backgroundColor: colors.surface,
  },
  actionButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  deleteAllButton: {
    backgroundColor: colors.error,
  },
  actionButtonText: {
    color: colors.onPrimary,
    fontSize: 12,
    fontWeight: '600',
  },
  deleteAllButtonText: {
    color: colors.onError,
  },
  list: {
    flex: 1,
    padding: 12,
  },
  filterCount: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: 8,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    backgroundColor: colors.surface,
    borderRadius: 6,
  },
  emptyIcon: {
    fontSize: 40,
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 6,
  },
  emptySubtext: {
    fontSize: 13,
    color: colors.textSecondary,
    textAlign: 'center',
    maxWidth: 350,
    lineHeight: 18,
  },
  entryCard: {
    backgroundColor: colors.surfaceElevated,
    borderRadius: 6,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 2,
    elevation: 1,
  },
  entryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
  },
  entryHeaderContent: {
    flex: 1,
  },
  sourceBadge: {
    alignSelf: 'flex-start',
    backgroundColor: colors.primarySurface,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.primary + '30',
    marginBottom: 4,
  },
  sourceBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.primary,
    letterSpacing: 0.2,
  },
  entryText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 2,
  },
  compareVsText: {
    fontSize: 12,
    color: colors.textSecondary,
    fontStyle: 'italic',
    marginBottom: 2,
  },
  matchInfoText: {
    fontSize: 11,
    color: colors.success,
    fontWeight: '500',
    marginBottom: 2,
  },
  entryNote: {
    fontSize: 12,
    color: colors.textSecondary,
    fontStyle: 'italic',
    marginBottom: 2,
  },
  entryMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 2,
  },
  entryDate: {
    fontSize: 10,
    color: colors.lightText,
  },
  cipherCountText: {
    fontSize: 10,
    color: colors.lightText,
  },
  expandIcon: {
    fontSize: 11,
    color: colors.textSecondary,
    paddingLeft: 10,
  },
  quickResults: {
    flexDirection: 'row',
    paddingHorizontal: 10,
    paddingBottom: 10,
    gap: 6,
  },
  quickResultItem: {
    flex: 1,
    backgroundColor: colors.mutedSurface,
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderRadius: 4,
    borderTopWidth: 2,
    borderTopColor: colors.primary,
    alignItems: 'center',
  },
  quickResultValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text,
  },
  quickResultName: {
    fontSize: 9,
    color: colors.textSecondary,
    marginTop: 2,
    textAlign: 'center',
  },
  tags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 10,
    paddingBottom: 8,
    gap: 5,
  },
  tag: {
    backgroundColor: colors.primarySurface,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  tagText: {
    fontSize: 10,
    color: colors.primary,
    fontWeight: '600',
  },
  expandedContent: {
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
    backgroundColor: colors.mutedSurface,
  },
  expandedTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  detailedResults: {
    maxHeight: 250,
  },
  detailedResultItem: {
    backgroundColor: colors.surface,
    padding: 8,
    borderRadius: 3,
    marginBottom: 4,
    borderLeftWidth: 2,
    borderLeftColor: colors.primary,
  },
  detailedResultHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  detailedResultName: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.text,
  },
  detailedResultValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.primary,
  },
  wordBreakdownMini: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  wordBreakdownText: {
    fontSize: 10,
    color: colors.textSecondary,
  },
  entryActions: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: colors.divider,
    alignItems: 'center',
  },
  noteButton: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRightWidth: 1,
    borderRightColor: colors.divider,
    backgroundColor: colors.surface,
  },
  noteButtonText: {
    fontSize: 12,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  noteButtonTextActive: {
    color: colors.primary,
  },
  loadButton: {
    flex: 1,
    padding: 10,
    alignItems: 'center',
    backgroundColor: colors.surface,
  },
  loadButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.primary,
  },
  deleteEntryButton: {
    padding: 10,
    paddingHorizontal: 14,
    borderLeftWidth: 1,
    borderLeftColor: colors.divider,
    backgroundColor: colors.surface,
  },
  deleteEntryText: {
    fontSize: 14,
  },
  noteEditContainer: {
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
    backgroundColor: colors.mutedSurface,
  },
  noteInput: {
    fontSize: 13,
    color: colors.text,
    backgroundColor: colors.inputBackground,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    padding: 10,
    minHeight: 60,
    outlineStyle: 'none',
  },
  noteEditActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 8,
  },
  noteCancelBtn: {
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  noteCancelText: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  noteSaveBtn: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: colors.primary,
    borderRadius: 4,
  },
  noteSaveText: {
    fontSize: 12,
    color: colors.onPrimary,
    fontWeight: '600',
  },
  noteDisplay: {
    padding: 8,
    paddingHorizontal: 10,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
    backgroundColor: colors.primarySurface,
  },
  noteDisplayText: {
    fontSize: 12,
    color: colors.text,
    lineHeight: 16,
  },
});
